import apiClient from "../../../../components/api/apiClient";
import { apiService } from "../../../../components/api/apiService";
import { isRFC4122Guid, isResourceAccessResourceId } from "../guidUtils";

const CREDENTIALS_BASE = "/api/ResourceAccess/credentials";
const RESOURCE_ACCESS_LIST_BASE = "/api/ResourceAccess/resource-access";
/** Resource groups (“applications” in OpenAPI) — list body uses `{ applications: [...] }`. */
const APPLICATIONS_BASE = "/api/ResourceAccess/applications";

function isHttpOk(status: number): boolean {
  return status === 200;
}

function isHttpCreateOk(status: number): boolean {
  return status === 200 || status === 201;
}

function resolveBackendBaseUrl(): string {
  const baseUrl = (process.env as Record<string, string | undefined>).REACT_APP_API_URL_backend;
  if (!baseUrl) {
    console.warn("⚠️ Missing environment variable: REACT_APP_API_URL_backend");
  }
  return baseUrl || process.env.REACT_APP_API_BASE_URL || "";
}

/** Parse application id from POST 201 `Location` (full URL or path, last segment = GUID). */
function idFromApplicationsLocation(loc: string | undefined): string | null {
  if (!loc || typeof loc !== "string") return null;
  try {
    const path = loc.trim().startsWith("http") ? new URL(loc.trim()).pathname : loc.trim();
    const parts = path.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    return last && isRFC4122Guid(last) ? last : null;
  } catch {
    return null;
  }
}

export type ApiResourceType = "DataModel" | "KnowledgeBase";

export interface ResourceAccessCredentialListItem {
  /** Mongo (or API) id for `/api/ResourceAccess/credentials/{id}`. */
  id: string;
  resourceType: ApiResourceType;
  resourceTypeName: string;
  label: string;
  createdOn: string;
  status: string;
  resourceGroupId: string;
  applicationId: string;
  clientId: string;
}

export interface ResourceAccessCredentialDetail {
  id: string;
  clientId: string;
  clientSecret: string;
  accessToken: string;
  resourceType: ApiResourceType;
  resourceTypeName: string;
  resourceGuids: string[];
  resourceGroupId: string;
  label: string;
  description: string;
  expiry: string;
  expiresAt: string;
  createdOn: string;
  issuedAt?: string;
  status: string;
  isActive: boolean;
  rateLimitPerMinute?: number;
}

export interface ApplicationResourceAccessPayload {
  resourceType: ApiResourceType;
  resourceGuids: string[];
  label: string;
  description: string;
}

function applicationResourceAccessPath(applicationId: string): string {
  return `${APPLICATIONS_BASE}/${encodeURIComponent(String(applicationId).trim())}/resource-access`;
}

function resourceAccessItemPath(resourceAccessId: string): string {
  return `${RESOURCE_ACCESS_LIST_BASE}/${encodeURIComponent(String(resourceAccessId).trim())}`;
}

function buildApplicationResourceAccessBody(
  payload: ApplicationResourceAccessPayload
): ApplicationResourceAccessPayload {
  const resourceGuids = payload.resourceGuids.map((g) => String(g).trim()).filter(isResourceAccessResourceId);
  if (resourceGuids.length === 0) {
    throw new Error(
      "At least one valid resource id is required (RFC GUID or data model `uuid` from /api/DataModels)."
    );
  }
  return {
    resourceType: payload.resourceType,
    resourceGuids,
    label: String(payload.label).trim(),
    description: String(payload.description ?? "").trim(),
  };
}

export interface ResourceAccessGroup {
  id: string;
  resourceGroupId: string;
  resourceGroupName: string;
  isActive: boolean;
  tenantId: string;
  createdOn: string;
  createdBy: string;
  updatedOn: string;
  updatedBy: string;
}

export interface UpsertResourceGroupPayload {
  resourceGroupName: string;
  isActive: boolean;
}

export interface CreateApplicationPayload {
  name: string;
  isActive: boolean;
  expiry?: string;
  rateLimitPerMinute?: number;
}

export const DEFAULT_APPLICATION_EXPIRY = "1000";
export const DEFAULT_APPLICATION_RATE_LIMIT_PER_MINUTE = 1000;

function pickStr(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = raw[k];
    if (v != null && String(v).trim() !== "") return String(v);
  }
  return "";
}

function pickBool(raw: Record<string, unknown>, ...keys: string[]): boolean {
  for (const k of keys) {
    const v = raw[k];
    if (v !== undefined && v !== null) return Boolean(v);
  }
  return false;
}

function asGuidList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x)).filter(Boolean);
}

function mapResourceType(v: unknown): ApiResourceType {
  const s = String(v ?? "");
  if (s === "KnowledgeBase" || s === "knowledgebase") return "KnowledgeBase";
  return "DataModel";
}

export function findCredentialsForApplication(
  app: ResourceAccessGroup,
  credentials: ResourceAccessCredentialListItem[]
): ResourceAccessCredentialListItem[] {
  const keys = new Set<string>();
  for (const k of [app.id, app.resourceGroupId]) {
    const t = String(k || "").trim().toLowerCase();
    if (t) keys.add(t);
  }
  if (keys.size === 0) return [];
  return credentials.filter((c) => {
    const credKeys = [c.resourceGroupId, c.applicationId]
      .map((s) => String(s || "").trim().toLowerCase())
      .filter(Boolean);
    return credKeys.some((k) => keys.has(k));
  });
}

function mapListItem(raw: Record<string, unknown>): ResourceAccessCredentialListItem {
  const applicationId = pickStr(raw, "applicationId", "ApplicationId");
  const resourceGroupId =
    pickStr(raw, "resourceGroupId", "ResourceGroupId") || applicationId;
  return {
    id: pickStr(raw, "id", "Id"),
    resourceType: mapResourceType(raw.resourceType ?? raw.ResourceType),
    resourceTypeName: pickStr(raw, "resourceTypeName", "ResourceTypeName"),
    label: pickStr(raw, "label", "Label"),
    createdOn: pickStr(raw, "createdOn", "CreatedOn"),
    status: pickStr(raw, "status", "Status"),
    resourceGroupId,
    applicationId: applicationId || resourceGroupId,
    clientId: pickStr(raw, "clientId", "ClientId"),
  };
}

export function mapCredentialDetail(raw: Record<string, unknown>): ResourceAccessCredentialDetail {
  const rl = raw.rateLimitPerMinute ?? raw.RateLimitPerMinute;
  const rateLimitPerMinute =
    rl != null && rl !== "" && Number.isFinite(Number(rl)) ? Math.max(0, Number(rl)) : undefined;
  return {
    id: pickStr(raw, "id", "Id"),
    clientId: pickStr(raw, "clientId", "ClientId"),
    clientSecret: pickStr(raw, "clientSecret", "ClientSecret"),
    accessToken: pickStr(raw, "accessToken", "AccessToken"),
    resourceType: mapResourceType(raw.resourceType ?? raw.ResourceType),
    resourceTypeName: pickStr(raw, "resourceTypeName", "ResourceTypeName"),
    resourceGuids: asGuidList(raw.resourceGuids ?? raw.ResourceGuids),
    resourceGroupId: pickStr(raw, "resourceGroupId", "ResourceGroupId", "applicationId", "ApplicationId"),
    label: pickStr(raw, "label", "Label"),
    description: pickStr(raw, "description", "Description") || "",
    expiry: pickStr(raw, "expiry", "Expiry"),
    expiresAt: pickStr(raw, "expiresAt", "ExpiresAt"),
    createdOn: pickStr(raw, "createdOn", "CreatedOn"),
    issuedAt: pickStr(raw, "issuedAt", "IssuedAt") || undefined,
    status: pickStr(raw, "status", "Status"),
    isActive: pickBool(raw, "isActive", "IsActive"),
    rateLimitPerMinute,
  };
}

function mapGroup(raw: Record<string, unknown>): ResourceAccessGroup {
  const explicitApplicationId = pickStr(raw, "applicationId", "ApplicationId");
  const resourceGroupId = pickStr(raw, "resourceGroupId", "ResourceGroupId");
  const idField = pickStr(raw, "id", "Id");
  const linkId = explicitApplicationId || resourceGroupId || idField;
  return {
    id: linkId,
    resourceGroupId: resourceGroupId || explicitApplicationId || idField,
    resourceGroupName: pickStr(raw, "resourceGroupName", "ResourceGroupName", "name", "Name"),
    isActive: pickBool(raw, "isActive", "IsActive"),
    tenantId: pickStr(raw, "tenantId", "TenantId"),
    createdOn: pickStr(raw, "createdOn", "CreatedOn"),
    createdBy: pickStr(raw, "createdBy", "CreatedBy"),
    updatedOn: pickStr(raw, "updatedOn", "UpdatedOn"),
    updatedBy: pickStr(raw, "updatedBy", "UpdatedBy"),
  };
}

function coerceCredentialList(data: unknown): ResourceAccessCredentialListItem[] {
  if (Array.isArray(data)) {
    return data.map((item) => mapListItem(item as Record<string, unknown>));
  }
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const list =
      o.resourceAccess ??
      o.ResourceAccess ??
      o.credentials ??
      o.Credentials ??
      o.data ??
      o.Data ??
      o.items ??
      o.Items;
    if (Array.isArray(list)) {
      return list.map((item) => mapListItem(item as Record<string, unknown>));
    }
  }
  return [];
}

function coerceGroupList(data: unknown): ResourceAccessGroup[] {
  if (Array.isArray(data)) {
    return data.map((item) => mapGroup(item as Record<string, unknown>));
  }
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const list =
      o.applications ??
      o.Applications ??
      o.applicationGroups ??
      o.ApplicationGroups ??
      o.groups ??
      o.Groups ??
      o.data ??
      o.Data;
    if (Array.isArray(list)) {
      return list.map((item) => mapGroup(item as Record<string, unknown>));
    }
  }
  return [];
}

export const resourceAccessService = {
  /** GET `/api/ResourceAccess/resource-access` — body `{ resourceAccess: [...] }`. */
  async listCredentials(): Promise<ResourceAccessCredentialListItem[]> {
    const data = await apiService.get(RESOURCE_ACCESS_LIST_BASE, {}, "backend");
    return coerceCredentialList(data);
  },

  /** GET `/api/ResourceAccess/credentials/{applicationId}` */
  async getCredentialByApplicationId(applicationId: string): Promise<ResourceAccessCredentialDetail> {
    const routeId = encodeURIComponent(String(applicationId ?? "").trim());
    if (!routeId) {
      throw new Error("Application id is required.");
    }
    const raw = await apiService.get(`${CREDENTIALS_BASE}/${routeId}`, {}, "backend");
    if (!raw || typeof raw !== "object") {
      throw new Error("Credential not found.");
    }
    return mapCredentialDetail(raw as Record<string, unknown>);
  },

  /** GET `/api/ResourceAccess/credentials/{id}` — `id` may be application id or credential id depending on caller. */
  async getCredential(id: string): Promise<ResourceAccessCredentialDetail> {
    return this.getCredentialByApplicationId(id);
  },

  /** POST `/api/ResourceAccess/applications/{applicationId}/resource-access` — success on 200 or 201. */
  async createApplicationResourceAccess(
    applicationId: string,
    payload: ApplicationResourceAccessPayload
  ): Promise<void> {
    const appId = String(applicationId ?? "").trim();
    if (!isRFC4122Guid(appId)) {
      throw new Error("applicationId must be a valid GUID.");
    }
    const body = buildApplicationResourceAccessBody(payload);
    const response = await apiClient.post(applicationResourceAccessPath(appId), body, {
      baseURL: resolveBackendBaseUrl(),
    });
    if (!isHttpCreateOk(response.status)) {
      throw new Error(`Create resource access failed (${response.status}).`);
    }
  },

  /** PUT `/api/ResourceAccess/resource-access/{id}` — closes UI only when status is 200. */
  async updateResourceAccess(resourceAccessId: string, payload: ApplicationResourceAccessPayload): Promise<void> {
    const id = String(resourceAccessId ?? "").trim();
    if (!id) {
      throw new Error("Resource access id is required.");
    }
    const body = buildApplicationResourceAccessBody(payload);
    const response = await apiClient.put(resourceAccessItemPath(id), body, {
      baseURL: resolveBackendBaseUrl(),
    });
    if (!isHttpOk(response.status)) {
      throw new Error(`Update resource access failed (${response.status}).`);
    }
  },

  /** DELETE `/api/ResourceAccess/resource-access/{id}` */
  async deleteCredential(resourceAccessId: string): Promise<void> {
    const id = String(resourceAccessId ?? "").trim();
    if (!id) {
      throw new Error("Resource access id is required.");
    }
    const response = await apiClient.delete(resourceAccessItemPath(id), {
      baseURL: resolveBackendBaseUrl(),
    });
    if (response.status !== 200 && response.status !== 204) {
      throw new Error(`Delete resource access failed (${response.status}).`);
    }
  },

  async rotateSecret(id: string): Promise<ResourceAccessCredentialDetail> {
    const raw = await apiService.post(`${CREDENTIALS_BASE}/${id}/rotate-secret`, {}, "backend");
    return mapCredentialDetail(raw as Record<string, unknown>);
  },

  async revokeCredential(id: string): Promise<ResourceAccessCredentialListItem> {
    const raw = await apiService.post(`${CREDENTIALS_BASE}/${id}/revoke`, {}, "backend");
    return mapListItem(raw as Record<string, unknown>);
  },

  async listGroups(): Promise<ResourceAccessGroup[]> {
    const data = await apiService.get(APPLICATIONS_BASE, {}, "backend");
    return coerceGroupList(data);
  },

  async getGroup(id: string): Promise<ResourceAccessGroup | null> {
    try {
      const raw = await apiService.get(`${APPLICATIONS_BASE}/${id}`, {}, "backend");
      if (!raw || typeof raw !== "object") return null;
      return mapGroup(raw as Record<string, unknown>);
    } catch {
      return null;
    }
  },

  async createGroup(payload: CreateApplicationPayload): Promise<ResourceAccessGroup> {
    const body = {
      name: String(payload.name).trim(),
      isActive: payload.isActive,
      expiry: payload.expiry ?? DEFAULT_APPLICATION_EXPIRY,
      rateLimitPerMinute: payload.rateLimitPerMinute ?? DEFAULT_APPLICATION_RATE_LIMIT_PER_MINUTE,
    };
    const response = await apiClient.post(APPLICATIONS_BASE, body, { baseURL: resolveBackendBaseUrl() });
    const raw = response.data;
    if (raw && typeof raw === "object" && Object.keys(raw as object).length > 0) {
      return mapGroup(raw as Record<string, unknown>);
    }
    const loc =
      (response.headers?.location as string | undefined) ??
      (response.headers?.Location as string | undefined);
    const createdId = idFromApplicationsLocation(loc);
    if (createdId) {
      const fetched = await resourceAccessService.getGroup(createdId);
      if (fetched) return fetched;
    }
    throw new Error(
      "Create application returned no JSON body and no usable Location header; cannot resolve created resource."
    );
  },

  async updateGroup(id: string, payload: UpsertResourceGroupPayload): Promise<ResourceAccessGroup | null> {
    const raw = await apiService.put(
      `${APPLICATIONS_BASE}/${id}`,
      { resourceGroupName: payload.resourceGroupName, isActive: payload.isActive },
      "backend"
    );
    if (raw == null || (typeof raw === "object" && raw !== null && Object.keys(raw as object).length === 0)) {
      return resourceAccessService.getGroup(id);
    }
    return mapGroup(raw as Record<string, unknown>);
  },

  async deleteGroup(id: string): Promise<void> {
    const trimmed = String(id ?? "").trim();
    await apiClient.delete(`${APPLICATIONS_BASE}/${trimmed}`, {
      baseURL: resolveBackendBaseUrl(),
    });
  },
};
